import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const formatMbValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return `${numeric.toFixed(2)} MB`;
};

const formatPercentValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return `%${numeric.toFixed(1)}`;
};

const formatRemaining = (seconds) => {
  const numeric = Number(seconds);
  if (!Number.isFinite(numeric) || numeric <= 0) return "-";
  const days = Math.floor(numeric / 86400);
  const hours = Math.floor((numeric % 86400) / 3600);
  const minutes = Math.floor((numeric % 3600) / 60);
  if (days > 0) return `${days}g ${hours}s ${minutes}dk`;
  if (hours > 0) return `${hours}s ${minutes}dk`;
  return `${minutes}dk`;
};

const resolvePresetValue = (value, options) => {
  const numeric = Number(value);
  if (options.includes(numeric)) return String(numeric);
  return "custom";
};

const statusTone = {
  healthy: "border-emerald-700/50 bg-emerald-950/20 text-emerald-200",
  warning: "border-amber-700/50 bg-amber-950/20 text-amber-200",
  critical: "border-rose-700/50 bg-rose-950/20 text-rose-200",
  n_a: "border-slate-700 bg-slate-900/40 text-slate-300",
};

export const LogCaptureControlPanel = ({
  loading,
  saving,
  dirty,
  form,
  meta,
  onFieldChange,
  onRefresh,
  onSave,
}) => {
  const durationOptions = meta?.options?.duration_options || [];
  const limitPresetOptions = meta?.options?.limit_preset_options || [];
  const normalStorage = meta?.normal_storage || {};
  const ultraStorage = meta?.ultra_storage || {};
  const normalPresetValue = resolvePresetValue(form?.normal_logs_limit_mb, limitPresetOptions);
  const ultraPresetValue = resolvePresetValue(form?.ultra_logs_limit_mb, limitPresetOptions);

  return (
    <section className="rounded-2xl border border-cyan-800/50 bg-slate-950/60 p-5" data-testid="audit-log-capture-panel">
      <div className="flex flex-wrap items-start justify-between gap-3" data-testid="audit-log-capture-header">
        <div data-testid="audit-log-capture-header-copy">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300" data-testid="audit-log-capture-kicker">Log Capture Control</p>
          <h3 className="mt-1 text-xl font-black text-slate-100" data-testid="audit-log-capture-title">Ultra detaylı log kontrolü</h3>
          <p className="mt-1 text-sm text-slate-300" data-testid="audit-log-capture-description">
            Mevcut normal log sistemi korunur. Ultra log ayrı dizinde, süreli ve limitli çalışır.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="audit-log-capture-actions">
          <Button type="button" variant="outline" onClick={onRefresh} disabled={loading || saving} data-testid="audit-log-capture-refresh-button">
            {loading ? "Yükleniyor..." : "Yenile"}
          </Button>
          <Button type="button" onClick={onSave} disabled={loading || saving} data-testid="audit-log-capture-save-button">
            {saving ? "Kaydediliyor..." : dirty ? "Değişiklikleri Kaydet" : "Kaydet"}
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-4" data-testid="audit-log-capture-form-grid">
        <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4" data-testid="audit-log-capture-toggle-card">
          <div className="flex items-center gap-3" data-testid="audit-log-capture-toggle-row">
            <Checkbox
              checked={Boolean(form?.ultra_log_enabled)}
              onCheckedChange={(checked) => onFieldChange("ultra_log_enabled", checked === true)}
              data-testid="audit-log-capture-ultra-toggle-checkbox"
            />
            <div>
              <p className="text-sm font-semibold text-slate-100" data-testid="audit-log-capture-ultra-toggle-label">Ultra detaylı log</p>
              <p className="text-xs text-slate-400" data-testid="audit-log-capture-ultra-toggle-helper">Tüm sistem için ek detay toplar.</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-300" data-testid="audit-log-capture-ultra-status">
            durum: {meta?.settings?.ultra_log_enabled ? "aktif" : "kapalı"}
          </p>
          <p className="text-xs text-slate-300" data-testid="audit-log-capture-ultra-remaining">
            kalan: {formatRemaining(ultraStorage?.remaining_seconds)}
          </p>
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4" data-testid="audit-log-capture-duration-card">
          <label className="space-y-2" data-testid="audit-log-capture-duration-field">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Açık kalma süresi</span>
            <select
              className="h-10 w-full rounded border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100"
              value={form?.duration_key || "1h"}
              onChange={(event) => onFieldChange("duration_key", event.target.value)}
              data-testid="audit-log-capture-duration-select"
            >
              {durationOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <p className="mt-3 text-xs text-slate-300" data-testid="audit-log-capture-expires-at">
            bitiş: {ultraStorage?.expires_at || "-"}
          </p>
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4" data-testid="audit-log-capture-normal-limit-card">
          <label className="space-y-2" data-testid="audit-log-capture-normal-limit-preset-field">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Normal log limiti (MB)</span>
            <select
              className="h-10 w-full rounded border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100"
              value={normalPresetValue}
              onChange={(event) => {
                if (event.target.value !== "custom") onFieldChange("normal_logs_limit_mb", Number(event.target.value));
              }}
              data-testid="audit-log-capture-normal-limit-preset-select"
            >
              {limitPresetOptions.map((option) => (
                <option key={option} value={String(option)}>{option} MB</option>
              ))}
              <option value="custom">Özel</option>
            </select>
          </label>
          <Input
            type="number"
            min="64"
            max="4096"
            value={form?.normal_logs_limit_mb ?? ""}
            onChange={(event) => onFieldChange("normal_logs_limit_mb", Number(event.target.value || 0))}
            className="mt-3 border-slate-700 bg-slate-950 text-slate-100"
            data-testid="audit-log-capture-normal-limit-input"
          />
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4" data-testid="audit-log-capture-ultra-limit-card">
          <label className="space-y-2" data-testid="audit-log-capture-ultra-limit-preset-field">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Ultra log limiti (MB)</span>
            <select
              className="h-10 w-full rounded border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100"
              value={ultraPresetValue}
              onChange={(event) => {
                if (event.target.value !== "custom") onFieldChange("ultra_logs_limit_mb", Number(event.target.value));
              }}
              data-testid="audit-log-capture-ultra-limit-preset-select"
            >
              {limitPresetOptions.map((option) => (
                <option key={option} value={String(option)}>{option} MB</option>
              ))}
              <option value="custom">Özel</option>
            </select>
          </label>
          <Input
            type="number"
            min="64"
            max="4096"
            value={form?.ultra_logs_limit_mb ?? ""}
            onChange={(event) => onFieldChange("ultra_logs_limit_mb", Number(event.target.value || 0))}
            className="mt-3 border-slate-700 bg-slate-950 text-slate-100"
            data-testid="audit-log-capture-ultra-limit-input"
          />
        </div>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-2" data-testid="audit-log-capture-storage-grid">
        {[normalStorage, ultraStorage].map((storage, index) => {
          const key = index === 0 ? "normal" : "ultra";
          return (
            <article key={key} className={`rounded-xl border p-4 ${statusTone[storage?.status] || statusTone.n_a}`} data-testid={`audit-log-capture-storage-card-${key}`}>
              <div className="flex flex-wrap items-start justify-between gap-2" data-testid={`audit-log-capture-storage-card-header-${key}`}>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em]" data-testid={`audit-log-capture-storage-label-${key}`}>{key === "normal" ? "Normal Log" : "Ultra Log"}</p>
                  <p className="mt-1 text-sm font-semibold" data-testid={`audit-log-capture-storage-path-${key}`}>{storage?.directory_path || "DB / external storage"}</p>
                </div>
                <span className="rounded border border-current px-2 py-1 text-[11px] uppercase" data-testid={`audit-log-capture-storage-status-${key}`}>{storage?.status || "n_a"}</span>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid={`audit-log-capture-storage-metrics-${key}`}>
                <p className="text-xs" data-testid={`audit-log-capture-storage-size-${key}`}>boyut: {formatMbValue(storage?.current_size_mb)}</p>
                <p className="text-xs" data-testid={`audit-log-capture-storage-limit-${key}`}>limit: {storage?.limit_mb ? `${storage.limit_mb} MB` : "-"}</p>
                <p className="text-xs" data-testid={`audit-log-capture-storage-usage-${key}`}>kullanım: {formatPercentValue(storage?.usage_pct)}</p>
                <p className="text-xs" data-testid={`audit-log-capture-storage-file-count-${key}`}>dosya: {storage?.file_count ?? 0}</p>
                <p className="text-xs" data-testid={`audit-log-capture-storage-oldest-${key}`}>en eski: {storage?.oldest_file_at || "-"}</p>
                <p className="text-xs" data-testid={`audit-log-capture-storage-newest-${key}`}>en yeni: {storage?.newest_file_at || "-"}</p>
              </div>
            </article>
          );
        })}
      </div>

      <div className="mt-4 grid gap-2 md:grid-cols-2" data-testid="audit-log-capture-meta-grid">
        <p className="text-xs text-slate-400" data-testid="audit-log-capture-enabled-by">aktif eden: {meta?.settings?.enabled_by_admin_email || "-"}</p>
        <p className="text-xs text-slate-400" data-testid="audit-log-capture-auto-disabled-reason">otomatik kapanma sebebi: {meta?.settings?.auto_disabled_reason || "-"}</p>
      </div>
    </section>
  );
};